import { Injectable, inject } from '@angular/core';

import { ICellRendererParams } from 'ag-grid-enterprise';

import { CamVatLevelsService } from '@camelot/services';
import { Unit } from '@camelot/services';

import { GridCommentRowComponent } from '../../components/ag-grid/comment-row/comment-row.component';
import { GridEmptyCellComponent } from '../../components/ag-grid/empty-cell/empty-cell.component';
import { GridUnitCellComponent, unitLabel } from '../../components/templates/unit-type/unit-type.component';
import { IFieldsOverrides } from '../../models/types';
import { GridRow } from './grid-row';
import { GridMetaData, ParameterType } from './metadata';
import { RowType } from './row-type';

const emptyCellForChapter = {
  cellRendererSelector: (params: ICellRendererParams) =>
    (params.data as GridRow | undefined)?.rowType === RowType.Chapter
      ? { component: GridEmptyCellComponent }
      : undefined,
};

export const unitColumnMetaData: GridMetaData = {
  enumInfo: { cellRenderer: GridUnitCellComponent, enum: Unit, labelFn: unitLabel },
  enumName: 'Unit',
  name: 'unit',
  nullable: true,
  shownByDefault: true,
  type: ParameterType.Enum,
};

@Injectable({
  providedIn: 'root',
})
export class CamGridRowCommonMetaDataService {
  private _vatLevelsService = inject(CamVatLevelsService);

  constructor() {
    this._vatLevelsService.fetchVatLevels$().subscribe();
  }

  // Les colonnes absentes de la liste passent en fin de grille, dans leur ordre de déclaration.
  public readonly columnsOrder: string[] = [
    'description',
    'unit',
    'quantity',
    'unitSellingPrice',
    'unitCostPrice',
    'exclVatTotal',
    'vatLevelId',
  ];

  // Pas de colonne prix ici : la vente valorise au prix de vente, l'achat au prix de revient.
  public readonly metaData: GridMetaData[] = [
    { name: 'description', shownByDefault: true, type: ParameterType.String },
    unitColumnMetaData,
    { name: 'quantity', nullable: true, shownByDefault: true, type: ParameterType.Number },
    { name: 'exclVatTotal', nullable: true, shownByDefault: true, type: ParameterType.Currency },
    {
      asyncEnumInfo: { numericId: true, values$: this._vatLevelsService.choices$ },
      name: 'vatLevelId',
      nullable: true,
      shownByDefault: true,
      type: ParameterType.AsyncEnum,
    },
  ];

  public sort(metaData: GridMetaData[]): GridMetaData[] {
    const rank = (col: GridMetaData) => {
      const index = this.columnsOrder.indexOf(col.name);
      return index === -1 ? this.columnsOrder.length : index;
    };
    return [...metaData].sort((a, b) => rank(a) - rank(b));
  }

  public readonly oneLineRowFieldsOverrides: IFieldsOverrides = {
    description: {
      cellRendererSelector: (params: ICellRendererParams) =>
        (params.data as GridRow | undefined)?.rowType === RowType.Comment
          ? { component: GridCommentRowComponent }
          : undefined,
      flex: 1,
    },
    exclVatTotal: emptyCellForChapter,
    quantity: emptyCellForChapter,
    unit: emptyCellForChapter,
    unitSellingPrice: emptyCellForChapter,
    vatLevelId: emptyCellForChapter,
  };
}
